import { isSuccess, isUnauthorized, type IHTTPResponse } from '@yimoka/shared';
import { action, define, observable } from '@yimoka/store';

import { authAccountEntity, type AuthAccountEntity } from '@/entities/auth-account';
import type { IAuthAccountOIDCRedirect } from '@/entities/auth-account.d';
import {
  clearAuthSession,
  getUserToken,
  setHydraSession,
} from '@/session/auth-session';
import {
  normalizeInternalRedirect,
  normalizeOIDCServerRedirect,
} from '@/utils/auth-redirect';

type Navigate = (to: string, options?: { replace?: boolean }) => unknown;
type OIDCFlowStatus = 'error' | 'idle' | 'invalid' | 'loading' | 'success';
type BrowserRedirect = (target: string) => void;

/** requiresPlatformLogin 兼容后端标准 401 与旧服务返回的中文登录提示。 */
function requiresPlatformLogin(response: Partial<IHTTPResponse<unknown>>) {
  return isUnauthorized(response) || `${response.msg ?? ''}`.includes('请先登录');
}

/** getOIDCRedirect 兼容旧 Logout 的 redirect_to 字段，并统一经过协议白名单。 */
function getOIDCRedirect(response?: IAuthAccountOIDCRedirect) {
  return normalizeOIDCServerRedirect(response?.redirect ?? response?.redirect_to);
}

/** OIDCAuthorizationBiz 编排 Hydra 登录、授权确认与登出 challenge 的 Web 闭环。 */
export class OIDCAuthorizationBiz {
  loginStatus: OIDCFlowStatus = 'idle';
  consentStatus: OIDCFlowStatus = 'idle';
  logoutStatus: OIDCFlowStatus = 'idle';
  accountLogoutStatus: OIDCFlowStatus = 'idle';

  /** 初始化 OIDC 页面状态，并允许测试注入隔离的 Entity 与浏览器跳转器。 */
  constructor(
    private readonly accountEntity: AuthAccountEntity = authAccountEntity,
    private readonly redirectBrowser: BrowserRedirect = target => window.location.assign(target),
  ) {
    define(this, {
      loginStatus: observable,
      consentStatus: observable,
      logoutStatus: observable,
      accountLogoutStatus: observable,
      mountLogin: action,
      mountConsent: action,
      mountLogout: action,
      logoutPlatformAccount: action,
    });
  }

  /** oidcAcceptLoginStore 暴露登录 challenge 操作状态。 */
  get oidcAcceptLoginStore() {
    return this.accountEntity.oidcAcceptLoginStore;
  }

  /** oidcAcceptConsentStore 暴露 consent challenge 操作状态。 */
  get oidcAcceptConsentStore() {
    return this.accountEntity.oidcAcceptConsentStore;
  }

  /** oidcAcceptLogoutStore 暴露 logout challenge 操作状态。 */
  get oidcAcceptLogoutStore() {
    return this.accountEntity.oidcAcceptLogoutStore;
  }

  /** platformLogoutStore 暴露启云平台账号退出操作状态。 */
  get platformLogoutStore() {
    return this.accountEntity.platformLogoutStore;
  }

  /** mountLogin 接受 login_challenge；无平台用户态时保留 challenge 返回登录页。 */
  mountLogin = async (navigate: Navigate, currentPath: string, search: string) => {
    if (this.loginStatus === 'loading') return;
    const challenge = new URLSearchParams(search)
      .get('login_challenge')
      ?.trim() ?? '';
    if (!challenge) {
      this.loginStatus = 'invalid';
      return;
    }
    if (!getUserToken()) {
      // 登录页返回后同一 Biz 实例还会继续处理原 challenge，不能把状态遗留为 loading。
      this.loginStatus = 'idle';
      this.redirectToPlatformLogin(navigate, currentPath);
      return;
    }

    this.loginStatus = 'loading';
    this.oidcAcceptLoginStore.setValues({ challenge });
    const response = await this.oidcAcceptLoginStore.fetch();
    if (requiresPlatformLogin(response)) {
      clearAuthSession();
      this.loginStatus = 'idle';
      this.redirectToPlatformLogin(navigate, currentPath);
      return;
    }
    if (!isSuccess(response)) {
      this.loginStatus = 'error';
      return;
    }

    const target = getOIDCRedirect(response.data);
    if (!target) {
      this.loginStatus = 'error';
      return;
    }

    // 与旧 Web 保持同名标记，避免升级期间其他前端逻辑无法识别 Hydra 会话。
    setHydraSession(true);
    this.loginStatus = 'success';
    this.performBrowserRedirect(target, 'login');
  };

  /** mountConsent 自动接受 openid scope，与旧仓库的 skipConsent 配套流程保持一致。 */
  mountConsent = async (navigate: Navigate, currentPath: string, search: string) => {
    if (this.consentStatus === 'loading') return;
    const challenge = new URLSearchParams(search)
      .get('consent_challenge')
      ?.trim() ?? '';
    if (!challenge) {
      this.consentStatus = 'invalid';
      return;
    }
    if (!getUserToken()) {
      // consent 深链也可能被直接打开，平台登录回来后必须允许重新发起接受请求。
      this.consentStatus = 'idle';
      this.redirectToPlatformLogin(navigate, currentPath);
      return;
    }

    this.consentStatus = 'loading';
    this.oidcAcceptConsentStore.setValues({
      challenge,
      scope: ['openid'],
      remember: true,
    });
    const response = await this.oidcAcceptConsentStore.fetch();
    if (requiresPlatformLogin(response)) {
      clearAuthSession();
      this.consentStatus = 'idle';
      this.redirectToPlatformLogin(navigate, currentPath);
      return;
    }
    if (!isSuccess(response)) {
      this.consentStatus = 'error';
      return;
    }

    const target = getOIDCRedirect(response.data);
    if (!target) {
      this.consentStatus = 'error';
      return;
    }

    this.consentStatus = 'success';
    this.performBrowserRedirect(target, 'consent');
  };

  /** mountLogout 接受 logout_challenge；该步骤不依赖本地平台 token。 */
  mountLogout = async (search: string) => {
    if (this.logoutStatus === 'loading') return;
    const challenge = new URLSearchParams(search)
      .get('logout_challenge')
      ?.trim() ?? '';
    if (!challenge) {
      this.logoutStatus = 'invalid';
      return;
    }

    this.logoutStatus = 'loading';
    this.oidcAcceptLogoutStore.setValues({ challenge });
    const response = await this.oidcAcceptLogoutStore.fetch();
    if (!isSuccess(response)) {
      this.logoutStatus = 'error';
      return;
    }

    // Hydra 已接受登出后立即移除兼容标记，即使后续响应地址异常也不能继续声称会话存在。
    setHydraSession(false);
    const target = getOIDCRedirect(response.data);
    if (!target) {
      this.logoutStatus = 'error';
      return;
    }

    this.logoutStatus = 'success';
    this.performBrowserRedirect(target, 'logout');
  };

  /** logoutPlatformAccount 从登出完成页继续退出启云账号，失败时保留本地会话以便重试。 */
  logoutPlatformAccount = async () => {
    if (this.accountLogoutStatus === 'loading') return false;
    this.accountLogoutStatus = 'loading';
    const response = await this.platformLogoutStore.fetch();
    if (!isSuccess(response)) {
      this.accountLogoutStatus = 'error';
      return false;
    }

    clearAuthSession();
    this.accountLogoutStatus = 'success';
    return true;
  };

  /** redirectToPlatformLogin 只把安全站内 OIDC 地址放入 redirect，避免旧实现的绝对地址被拒绝。 */
  private redirectToPlatformLogin(navigate: Navigate, currentPath: string) {
    const safeCurrentPath = normalizeInternalRedirect(currentPath) ?? '/oauth/login';
    navigate(`/login?redirect=${encodeURIComponent(safeCurrentPath)}`, { replace: true });
  }

  /** performBrowserRedirect 执行授权服务器返回的整页跳转，并把同步异常转成可重试状态。 */
  private performBrowserRedirect(target: string, flow: 'consent' | 'login' | 'logout') {
    try {
      this.redirectBrowser(target);
    } catch {
      if (flow === 'login') this.loginStatus = 'error';
      if (flow === 'consent') this.consentStatus = 'error';
      if (flow === 'logout') this.logoutStatus = 'error';
    }
  }
}

export const oidcAuthorizationBiz = new OIDCAuthorizationBiz();
